/** Modal para crear un comité nuevo + admin inicial. */
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Building2 } from "lucide-react";

import { useToast } from "../../components/Toast";
import { Modal } from "../../components/Modal";
import { Button, Input, useDescarteProtegido, useHayCambios } from "../../components/ui";
import { ApiError } from "../../lib/api";
import { crearEmpresa } from "./api";

const schema = z.object({
  nombre: z.string().min(1, "Requerido").max(100),
  rut: z
    .string()
    .min(8, "RUT requerido")
    .max(12)
    .regex(/^[0-9.\-Kk]+$/, "Formato inválido (usar dígitos, puntos y guión)"),
  email: z.string().email("Email inválido"),
  direccion: z.string().max(200).optional().or(z.literal("")),
  telefono: z.string().max(20).optional().or(z.literal("")),
  giro: z.string().max(200).optional().or(z.literal("")),
  admin_username: z.string().min(3, "Mínimo 3 caracteres").max(64),
  admin_nombre: z.string().min(1, "Requerido").max(120),
  admin_email: z.string().email("Email inválido"),
  admin_password: z
    .string()
    .min(8, "Mínimo 8 caracteres")
    .max(128, "Máximo 128 caracteres"),
});

type FormValues = z.infer<typeof schema>;

const FORM_ID = "crear-empresa-form";

export function CrearEmpresaModal({
  onClose,
}: {
  onClose: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      nombre: "",
      rut: "",
      email: "",
      direccion: "",
      telefono: "",
      giro: "",
      admin_username: "",
      admin_nombre: "",
      admin_email: "",
      admin_password: "",
    },
  });

  const mutation = useMutation({
    mutationFn: crearEmpresa,
    onSuccess: (data) => {
      toast.success(`Comité ${data.empresa.nombre} creado`);
      queryClient.invalidateQueries({ queryKey: ["superadmin", "empresas"] });
      onClose();
      navigate(`/superadmin/empresas/${data.empresa.id}`);
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "No se pudo crear"),
  });

  const onSubmit = (values: FormValues): void => {
    mutation.mutate({
      nombre: values.nombre,
      rut: values.rut,
      email: values.email,
      direccion: values.direccion || null,
      telefono: values.telefono || null,
      giro: values.giro || null,
      admin_username: values.admin_username,
      admin_nombre: values.admin_nombre,
      admin_email: values.admin_email,
      admin_password: values.admin_password,
    });
  };

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  // watch() da todos los campos del formulario (react-hook-form).
  const hayCambios = useHayCambios([watch()]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mutation.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Nuevo comité"
      subtitle="Tenant + admin inicial en una sola operación"
      icon={Building2}
      size="xl"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={intentarCerrar}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button type="submit" form={FORM_ID} disabled={mutation.isPending}>
            {mutation.isPending ? "Creando…" : "Crear comité"}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)} className="grid gap-4">
        <Section title="Datos del comité">
          <Input
            label="Nombre"
            type="text"
            {...register("nombre")}
            error={errors.nombre?.message}
            placeholder="Comité APR Las Lomas"
            autoFocus
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="RUT"
              type="text"
              {...register("rut")}
              error={errors.rut?.message}
              placeholder="76.123.456-7"
            />
            <Input
              label="Email del comité"
              type="email"
              {...register("email")}
              error={errors.email?.message}
              placeholder="contacto@comite.cl"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Teléfono"
              type="text"
              {...register("telefono")}
              error={errors.telefono?.message}
              placeholder="+56 9 1234 5678"
            />
            <Input
              label="Giro"
              type="text"
              {...register("giro")}
              error={errors.giro?.message}
              placeholder="Servicios sanitarios rurales"
            />
          </div>
          <Input
            label="Dirección"
            type="text"
            {...register("direccion")}
            error={errors.direccion?.message}
            placeholder="Camino Rural km 5, Comuna"
          />
        </Section>

        <Section title="Admin inicial del comité">
          <p className="text-xs text-neutral-500 -mt-2 mb-1">
            Este usuario recibe rol <code>admin</code>, NO superadmin. Sólo
            verá datos de su comité. Puedes cambiarle el password después con
            login-as.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Nombre del admin"
              type="text"
              {...register("admin_nombre")}
              error={errors.admin_nombre?.message}
              placeholder="Juan Pérez"
            />
            <Input
              label="Username"
              type="text"
              {...register("admin_username")}
              error={errors.admin_username?.message}
              placeholder="jperez"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Email del admin"
              type="email"
              {...register("admin_email")}
              error={errors.admin_email?.message}
              placeholder="admin@comite.cl"
            />
            <Input
              label="Password inicial"
              type="text"
              {...register("admin_password")}
              error={errors.admin_password?.message}
              className="font-mono"
              placeholder="mín. 8 caracteres"
            />
          </div>
        </Section>
      </form>
      {dialogoDescarte}
    </Modal>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <fieldset className="space-y-3">
      <legend className="text-sm font-semibold text-neutral-900">{title}</legend>
      {children}
    </fieldset>
  );
}
